import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { ComposeArchetype } from "@/lib/manufacturing/composePlan";
import {
  applyNamingPattern,
  DEFAULT_CODE_PATTERN,
  DEFAULT_NAME_PATTERN,
} from "@/lib/manufacturing/composePlan/skuPattern";
import { withGeneratedSellableSkus } from "@/lib/manufacturing/composePlan/sellableSkus";
import { parseSizeList } from "@/lib/manufacturing/groupMembers";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import { ManufacturingModelsComposeSlotsEditor } from "./ManufacturingModelsComposeSlotsEditor";
import { ManufacturingModelsSeriesWizardSizesField } from "./ManufacturingModelsSeriesWizardSizesField";

export function ManufacturingModelsComposeDefineForm({
  entryMode,
  archetype,
  stylesRaw,
  groups,
  briefRaw,
  extractBusy,
  onStylesRaw,
  onBriefRaw,
  onPatch,
  onExtractBrief,
  onGenerateError,
}: {
  entryMode: "bottom_up" | "top_down";
  archetype: ComposeArchetype;
  stylesRaw: string;
  groups: ReadonlyArray<MfgRecipeGroupSummary>;
  briefRaw: string;
  extractBusy?: boolean;
  onStylesRaw: (v: string) => void;
  onBriefRaw: (v: string) => void;
  onPatch: (patch: Partial<ComposeArchetype>) => void;
  onExtractBrief?: () => void;
  onGenerateError?: (msg: string | null) => void;
}) {
  function generateSellableSkus() {
    const built = withGeneratedSellableSkus(archetype, stylesRaw);
    if (!built.ok) {
      onGenerateError?.(built.error);
      return;
    }
    onGenerateError?.(null);
    onPatch({
      styles: built.archetype.styles,
      skuListRaw: built.archetype.skuListRaw,
    });
  }

  const firstSize = parseSizeList(archetype.sizesRaw)[0] ?? "10x12";
  const firstStyle =
    stylesRaw
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .find(Boolean) ?? "Utility";
  const previewVars = {
    CODE: archetype.seriesCode.trim() || "CL",
    SIZE: firstSize,
    STYLE: firstStyle,
    SERIES: archetype.seriesName.trim() || "Classic",
  };
  const codePreview = applyNamingPattern(
    archetype.namingPattern.trim() || DEFAULT_CODE_PATTERN,
    previewVars,
  );
  const namePreview = applyNamingPattern(
    (archetype.namePattern ?? "").trim() || DEFAULT_NAME_PATTERN,
    previewVars,
  );

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Plain-language brief (optional)
        </div>
        <textarea
          className={`${inputClass} mt-1 min-h-[72px]`}
          value={briefRaw}
          onChange={(e) => onBriefRaw(e.target.value)}
          placeholder="Describe the series, styles, sizes, slot fills, and size rules…"
        />
        {onExtractBrief ? (
          <button
            type="button"
            className={`${ghostBtnClass} mt-2`}
            disabled={extractBusy || !briefRaw.trim()}
            onClick={onExtractBrief}
          >
            {extractBusy ? "Extracting…" : "Fill form from brief"}
          </button>
        ) : null}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="text-slate-600 dark:text-slate-300">Recipe group</span>
          <select
            className={`${inputClass} mt-1`}
            value={archetype.recipe_group_id ?? ""}
            onChange={(e) => onPatch({ recipe_group_id: e.target.value || null })}
          >
            <option value="">— none —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.code} · {g.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-500">
            Required for Models filters — committed SKUs inherit this group.
          </p>
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Series name</span>
          <input
            className={`${inputClass} mt-1`}
            value={archetype.seriesName}
            onChange={(e) => onPatch({ seriesName: e.target.value })}
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Series / group code</span>
          <input
            className={`${inputClass} mt-1`}
            value={archetype.seriesCode}
            onChange={(e) => onPatch({ seriesCode: e.target.value })}
            placeholder="CL"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">SKU code pattern</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={archetype.namingPattern}
            onChange={(e) => onPatch({ namingPattern: e.target.value })}
            placeholder="{SIZE_COMPACT}-{CODE}-{STYLE_ABBREV}"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">SKU name pattern</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={archetype.namePattern ?? DEFAULT_NAME_PATTERN}
            onChange={(e) => onPatch({ namePattern: e.target.value })}
            placeholder="{SIZE}-{SERIES}-{STYLE}"
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Tokens: {"{SIZE}"} {"{SIZE_COMPACT}"} {"{CODE}"}/{"{GROUP}"} {"{SERIES}"} {"{STYLE}"}{" "}
        {"{STYLE_ABBREV}"}. Styles may use Utility=UT for a custom abbrev. Preview:{" "}
        <span className="font-mono text-slate-700 dark:text-slate-200">
          {codePreview}
        </span>{" "}
        · {namePreview}
      </p>

      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Size recognition (for AI)</span>
        <textarea
          className={`${inputClass} mt-1 min-h-[56px]`}
          value={archetype.sizeAxisHint}
          onChange={(e) => onPatch({ sizeAxisHint: e.target.value })}
          placeholder="WIDTH×LENGTH (W×L). 10x12 means 10 wide by 12 long."
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Sent with the brief so the model treats spoken/written Y×Y as W×L, not L×W.
        </p>
      </label>

      <ManufacturingModelsSeriesWizardSizesField
        sizesRaw={archetype.sizesRaw}
        onSizesRawChange={(next) => onPatch({ sizesRaw: next })}
      />

      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Styles</span>
        <textarea
          className={`${inputClass} mt-1 min-h-[56px]`}
          value={stylesRaw}
          onChange={(e) => onStylesRaw(e.target.value)}
          placeholder="style,style,style..."
        />
        <p className="mt-1 text-[11px] text-slate-500">
          One style per line, or comma-separated. Suggested: style,style,style...
        </p>
      </label>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Sellable SKUs {entryMode === "top_down" ? "(required)" : "(generated list)"}
          </span>
          <button type="button" className={mfgPrimaryBtnClass} onClick={generateSellableSkus}>
            Generate SKUs from series × sizes × styles
          </button>
        </div>
        <textarea
          className={`${inputClass} min-h-[120px] font-mono text-xs`}
          value={archetype.skuListRaw}
          onChange={(e) => onPatch({ skuListRaw: e.target.value })}
          placeholder={"CODE-SIZE-STYLE | Series size style\nCODE-SIZE-STYLE | Series size style"}
        />
        <p className="text-[11px] text-slate-500">
          {entryMode === "bottom_up"
            ? "Generate fills this list for review; Expand still fans out from sizes × styles. Top-down Expand uses this list."
            : "Generate fills every size×style product, or paste CODE | Name lines."}
        </p>
      </div>

      <ManufacturingModelsComposeSlotsEditor
        slots={archetype.slots}
        onChange={(slots) => onPatch({ slots })}
      />
    </div>
  );
}
